import { useRef, useState, useEffect, useCallback } from 'react'
import FileService from 'services/fileService'
import { getDeviceType } from 'utils'
import { DEVICE_TYPES_ENUM } from 'constants/enums'

export const useCamera = (isOpen: boolean) => {
  const [cameraIsLoading, setCameraIsLoading] = useState<boolean>(false)
  const [imagePreview, setImagePreview] = useState<string[]>([])
  const [imageFiles, setImageFiles] = useState<File[]>([])

  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const startCamera = useCallback(async () => {
    if (videoRef && isOpen) {
      try {
        setCameraIsLoading(true)
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        })
        videoRef!.current!.srcObject = stream
      } catch (err) {
        console.log(err)
      } finally {
        setCameraIsLoading(false)
      }
    }
  }, [isOpen])

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream
      const tracks = stream.getTracks()

      tracks.forEach((track) => {
        track.stop()
      })

      videoRef.current.srcObject = null
    }
  }

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    const width = videoRef.current.videoWidth
    const height = videoRef.current.videoHeight
    const ctx = canvasRef.current.getContext('2d')

    if (!ctx) return

    canvasRef.current.width = width
    canvasRef.current.height = height

    ctx.drawImage(videoRef.current, 0, 0, width, height)

    const image = canvasRef.current.toDataURL('image/png')
    const imageFile = FileService.dataURLtoFile(image, 'userPhoto')
    setImagePreview([image])
    setImageFiles([imageFile])
  }

  const handleChangeEvent = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length) {
      const files = Array.from(e.target.files)
      const previews = await Promise.all(files.map((file) => FileService.imageToBase64(file)))
      setImagePreview(previews)
      setImageFiles(files)
    }
  }

  const selectImage = () => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  const removeImage = (index?: number) => {
    if (typeof index === 'number') {
      setImagePreview((prev) => prev.filter((_, i) => i !== index))
      setImageFiles((prev) => prev.filter((_, i) => i !== index))
    } else {
      setImagePreview([])
      setImageFiles([])
    }
  }

  const resetCamera = () => {
    removeImage()
    startCamera()
  }

  useEffect(() => {
    if (isOpen) {
      if (getDeviceType() === DEVICE_TYPES_ENUM.DESKTOP) {
        startCamera()
      } else {
        inputRef.current?.click()
      }
    }

    return () => stopCamera()
  }, [isOpen, startCamera])

  return {
    cameraIsLoading,
    imagePreview,
    imageFiles,
    videoRef,
    canvasRef,
    inputRef,
    takePhoto,
    handleChangeEvent,
    selectImage,
    removeImage,
    resetCamera,
    setCameraIsLoading,
    stopCamera,
  }
}
